import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquare, Search, Map, ShieldCheck } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { SmartTriage } from "@/components/SmartTriage";
import { Blob } from "@/components/Decor";
import { IMAGES } from "@/data/content";

const STEPS = [
  { icon: MessageSquare, tone: "#C26D52", title: "Αφήνετε το μήνυμά σας", desc: "Πείτε μας λίγα λόγια για την επιχείρηση και τι θέλετε να βελτιώσετε ή να πετύχετε." },
  { icon: Search, tone: "#2C4A6F", title: "Συζητάμε σε βάθος", desc: "Κατανοούμε την κατάσταση, τους στόχους και τις προτεραιότητες της επιχείρησής σας." },
  { icon: Map, tone: "#5A7265", title: "Πρόταση πορείας", desc: "Λαμβάνετε μια ξεκάθαρη εικόνα για το πού υπάρχει περιθώριο ανάπτυξης και πώς θα ξεκινούσε η συνεργασία." },
  { icon: ShieldCheck, tone: "#C5A059", title: "Χωρίς δέσμευση", desc: "Η πρώτη συζήτηση είναι διερευνητική. Αποφασίζετε εσείς αν και πώς συνεχίζουμε." },
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  const interest = searchParams.get("interest") || "";
  const [prefill, setPrefill] = useState(null);

  return (
    <div data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        eyebrow="Επικοινωνία"
        tone="#C26D52"
        image={IMAGES.workspace}
        title={
          <>
            Ας μιλήσουμε για το <span className="italic text-[#C26D52]">επόμενο βήμα</span> σας.
          </>
        }
        desc="Στείλτε μας το μήνυμά σας και θα επικοινωνήσουμε μαζί σας για μια πρώτη συζήτηση — χωρίς καμία δέσμευση."
      />

      <section className="relative pb-24 lg:pb-32 pt-8 overflow-hidden">
        <Blob color="rgba(194,109,82,0.12)" className="bottom-0 left-[-10%]" size={600} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-4 lg:sticky lg:top-32 self-start">
              <p className="font-mono-brand text-[11px] tracking-[0.3em] uppercase text-[#C26D52]">Τι Ακολουθεί</p>
              <div className="mt-8 relative">
                <div className="absolute left-[23px] top-6 bottom-6 w-px bg-gradient-to-b from-[#C26D52] via-[#2C4A6F] to-[#C5A059] opacity-40" />
                <div className="space-y-4">
                  {STEPS.map((s, i) => (
                    <div key={s.title} data-testid={`contact-step-${i}`} className="relative flex gap-5 rounded-[22px] glass p-5 surface-hover">
                      <span className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-md" style={{ background: s.tone }}>
                        <s.icon className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-[#0E1B31]">{s.title}</h3>
                        <p className="mt-1.5 text-[13px] leading-relaxed text-[#3B4A5E]">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="mt-8 text-xs leading-relaxed text-[#6B7A8F]">
                Κάθε συνεργασία ξεκινά με ανάλυση. Δεν προτείνουμε λύσεις πριν κατανοήσουμε σε βάθος την επιχείρηση και την αγορά σας.
              </p>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-8">
              <SmartTriage onApply={(p) => setPrefill({ ...p, _t: Date.now() })} />
              <div className="rounded-[32px] bg-white border border-[#0E1B31]/[0.07] p-8 lg:p-12 shadow-[0_40px_90px_-50px_rgba(14,27,49,0.4)]">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#0E1B31]">Φόρμα επικοινωνίας</h2>
                  <span className="hidden sm:inline-flex rounded-full bg-[#EAF0EC] px-3 py-1.5 text-[11px] font-medium text-[#4A5F54]">Πρώτη συζήτηση χωρίς δέσμευση</span>
                </div>
                <ContactForm initialInterest={interest} prefill={prefill} key={interest} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
